import i18n from "./index";

const dynamicPatterns = [
  [/^Categories \((\d+)\)$/, "Categories", (label, count) => `${label} (${count})`],
  [/^Foods in Category\s+(.+?)\s+\((\d+)\)$/, "Foods in Category", (label, category, count) => `${label} ${category} (${count})`],
  [/^Banners \((\d+)\)$/, "Banners", (label, count) => `${label} (${count})`],
  [/^Add-ons \((\d+)\)$/, "Add-ons", (label, count) => `${label} (${count})`],
  [/^Live on QR: (\d+)$/, "Live on QR", (label, count) => `${label}: ${count}`],
  [/^View items \((\d+)\)$/, "View", (label, count) => `${label} (${count})`],
  [/^View & add items \((\d+)\)$/, "View & add items", (label, count) => `${label} (${count})`],
  [/^Existing Users \((\d+)\)$/, "Existing Users", (label, count) => `${label} (${count})`],
  [/^Reservations \((\d+)\)$/, "Reservations", (label, count) => `${label} (${count})`],
  [/^Table \((\d+)\)$/, "Tables", (label, count) => `${label} (${count})`],
  [/^Table (\d+)$/, "Table", (label, number) => `${label} ${number}`],
  [/^Order #(\d+)$/, "Order", (label, number) => `${label} #${number}`],
  [/^Live: (\d+)\/(\d+)$/, "Live", (label, current, total) => `${label}: ${current}/${total}`],
  [/^(\d+) currently active$/, "currently active", (label, count) => `${count} ${label}`],
  [/^(\d+) refunded$/, "refunded", (label, count) => `${count} ${label}`],
  [/^Average (.+)$/, "Average", (label, amount) => `${label} ${amount}`],
  [/^(\d+) orders\s+[^\p{L}\p{N}]+\s*(.+)$/u, "orders", (label, count, amount) => `${count} ${label} · ${amount}`],
  [/^(\d+) total takeaway orders$/, "total takeaway orders", (label, count) => `${count} ${label}`],
  [/^Notifications, (\d+) unread$/, "unread notifications", (label, count) => `${count} ${label}`],
  [/^Map Add-ons: (.+)$/, "Map Add-ons", (label, name) => `${label}: ${name}`],
  [/^Remove slot (\d+)$/, "Remove slot", (label, number) => `${label} ${number}`],
  [/^(From|To), time slot (\d+)$/, "time slot", (label, side, number) => `${i18n.t(side, { defaultValue: side })}, ${label} ${number}`],
  [/^(\d+) new orders?$/, "new orders", (label, count) => `${count} ${label}`],
  [/^Table T-(.+) Reservations$/, "Table reservations", (label, table) => `${label}: T-${table}`],
  [/^Latest (\d+) orders$/, "Latest {{count}} orders", (_label, count) => i18n.t("Latest {{count}} orders", { count })],
  [/^(\d+) orders?$/, "orders", (label, count) => `${count} ${label}`],
  [/^(\d+) orders? found$/, "orders found", (label, count) => `${count} ${label}`],
  [/^(Every day|Mon–Fri|Monday–Friday)\s*·\s*(.+)$/u, "schedule separator", (_label, days, time) => `${i18n.t(days, { defaultValue: days })} · ${time}`],
  [/^Generated (.+)$/, "Generated", (label, value) => `${label} ${value}`],
  [/^By (.+)$/, "By", (label, value) => `${label} ${value}`],
  [/^Bill #(.+) sent to (.+)$/, "Bill sent", (_label, bill, mobile) => i18n.t("Bill {{bill}} sent to {{mobile}}", { bill, mobile })],
  [/^(.+) updated\.$/, "Order updated", (_label, token) => i18n.t("{{token}} updated.", { token })],
  [/^Takeaway (.+) created\.$/, "Takeaway created", (_label, token) => i18n.t("Takeaway {{token}} created.", { token })],
  [/^User '(.+)' added successfully\.$/, "User added", (_label, name) => i18n.t("User '{{name}}' added successfully.", { name })],
  [/^Category '(.+)' added successfully\.$/, "Category added", (_label, name) => i18n.t("Category '{{name}}' added successfully.", { name })],
  [/^Food Item '(.+)' added successfully\.$/, "Food item added", (_label, name) => i18n.t("Food Item '{{name}}' added successfully.", { name })],
  [/^Addon group '(.+)' saved successfully\.$/, "Add-on saved", (_label, name) => i18n.t("Addon group '{{name}}' saved successfully.", { name })],
  [/^No (live|history|cancelled) takeaway orders found\.$/, "No takeaway orders", (_label, view) => i18n.t("No {{view}} takeaway orders found.", { view: i18n.t(view, { defaultValue: view }) })],
  [/^Table (.+) bill settled! Returning to Table View\.\.\.$/, "Table bill settled", (_label, table) => i18n.t("Table {{table}} bill settled! Returning to Table View...", { table })],
  [/^Table (.+) already exists for this restaurant\.$/, "Table exists", (_label, table) => i18n.t("Table {{table}} already exists for this restaurant.", { table })],
];

const translateExact = (text) => i18n.t(text, { defaultValue: text });

export const translateUiText = (value) => {
  if (!value || i18n.resolvedLanguage === "en") return value;
  const source = String(value);
  const trimmed = source.trim();
  if (!trimmed) return source;

  const exact = translateExact(trimmed);
  if (exact !== trimmed) return source.replace(trimmed, exact);

  // Text inside action buttons often starts with an emoji/icon. Translate the
  // readable label while preserving the icon exactly as rendered.
  const decorated = trimmed.match(/^([^\p{L}\p{N}]*)([\p{L}\p{N}].*)$/u);
  if (decorated?.[1]) {
    const translatedLabel = translateExact(decorated[2]);
    if (translatedLabel !== decorated[2]) {
      return source.replace(trimmed, `${decorated[1]}${translatedLabel}`);
    }
  }

  for (const [pattern, key, format] of dynamicPatterns) {
    const match = trimmed.match(pattern);
    if (match) return source.replace(trimmed, format(i18n.t(key, { defaultValue: key }), ...match.slice(1)));
  }
  return source;
};
